export type Status = "permitted" | "in_progress" | "blocked";
export type DecisionState = "granted" | "refused";

export type Cap =
  | "area.read"
  | "area.create"
  | "area.address.edit"
  | "area.geometry.edit"
  | "approval.record"
  | "approval.internal_signoff"
  | "evidence.open"
  | "evidence.delete"
  | "user.manage";

export type Pt = [number, number];
export type Poly = Pt[][];

export type Me = {
  user: { id: number; display_name: string; email: string; active: number };
  roles: string[];
  capabilities: Cap[];
};

export type DocumentCategory = "Telkomsel" | "TIF";

export const DOCUMENT_CATEGORIES: DocumentCategory[] = ["Telkomsel", "TIF"];

/**
 * The work axis (#49): what the area is drawn for, beside the document
 * category. Label-only -- grouping for the Dashboard, zero derivation or
 * permission effect. A sub belongs to exactly one parent.
 */
export const WORK_CATEGORIES = {
  Telkomsel: ["JPP PT3", "NODE-B"],
  Mitratel: ["Ring 1", "Ring 2"],
  Telkom: ["HEM", "OLO"],
} as const;
export type WorkCategory = keyof typeof WORK_CATEGORIES;
export type WorkSubcategory = (typeof WORK_CATEGORIES)[WorkCategory][number];

export const WORK_SUBCATEGORIES: WorkSubcategory[] = Object.values(WORK_CATEGORIES).flat();

export type AreaRow = {
  area_id: number;
  document_category: DocumentCategory | null;
  work_category: WorkCategory | null;
  work_subcategory: WorkSubcategory | null;
  kelurahan: string;
  kecamatan: string;
  rw: string | null;
  rt: string | null;
  postcode: string | null;
  address_detail: string | null;
  kabupaten_kota: string | null;
  status: Status;
  required_count: number;
  granted_count: number;
  refused_count: number;
};

export type EvidenceView = {
  id: number;
  kind: "photo" | "document";
  content_type: string | null;
  byte_size: number | null;
  content_sha256: string | null;
  landed: boolean;
  upload_attempts: number;
  last_error: string | null;
};

export type DecisionView = {
  id: number;
  approval_slot_id: number;
  supersedes_id: number | null;
  state: DecisionState;
  note: string | null;
  effective_at: string;
  created_at: string;
  author: string;
  synthetic: boolean;
  evidence: EvidenceView[];
};

export type SlotView = {
  slot_id: number;
  key: string;
  label: string;
  kind: "consent" | "attestation" | "internal";
  display_order: number;
  evidence_hint: string;
  plot_id: number | null;
  plot_label: string | null;
  owner_id: number | null;
  owner_name: string | null;
  land_right: string | null;
  land_registered: number | null;
  land_evidence_ref: string | null;
  current_state: DecisionState | null;
  current_decision_id: number | null;
  decision_count: number;
  decisions: DecisionView[];
};

export type AreaDetail = {
  area_id: number;
  document_category: DocumentCategory | null;
  work_category: WorkCategory | null;
  work_subcategory: WorkSubcategory | null;
  kelurahan: string;
  kecamatan: string;
  rw: string | null;
  rt: string | null;
  postcode: string | null;
  address_detail: string | null;
  kabupaten_kota: string | null;
  status: Status;
  required_count: number;
  granted_count: number;
  refused_count: number;
  geometry: Poly;
  label: Pt;
  area_m2: number;
  plot_count: number;
  plots: { id: number; label: string | null }[];
  drawn_by: string | null;
  slots: SlotView[];
};

export type PlotView = {
  id: number;
  label: string | null;
  land_right: string | null;
  registered: number;
  evidence_ref: string | null;
  owners: string[];
  geometry: Poly;
};

export type Dashboard = {
  totals: Record<Status, number>;
  areas: number;
  required: number;
  granted: number;
  refused: number;
  pending_evidence: number;
  by_kecamatan: {
    kecamatan: string;
    total: number;
    permitted: number;
    in_progress: number;
    blocked: number;
  }[];
  by_work_category: {
    work_category: WorkCategory;
    work_subcategory: WorkSubcategory;
    total: number;
    permitted: number;
    in_progress: number;
    blocked: number;
  }[];
  work_sections: {
    work_category: WorkCategory;
    subcategories: WorkSubcategory[];
    total: number;
    permitted: number;
    in_progress: number;
    blocked: number;
    required: number;
    granted: number;
    refused: number;
    by_subcategory: {
      work_subcategory: WorkSubcategory;
      total: number;
      permitted: number;
      in_progress: number;
      blocked: number;
    }[];
    by_kecamatan: {
      kecamatan: string;
      total: number;
      permitted: number;
      in_progress: number;
      blocked: number;
    }[];
  }[];
  unfiled_work: {
    total: number;
    permitted: number;
    in_progress: number;
    blocked: number;
  };
  region_sections: {
    gm: string;
    total: number;
    permitted: number;
    in_progress: number;
    blocked: number;
    areas: {
      area: string;
      total: number;
      permitted: number;
      in_progress: number;
      blocked: number;
      by_subcategory: {
        work_subcategory: string;
        total: number;
        permitted: number;
        in_progress: number;
        blocked: number;
      }[];
    }[];
  }[];
  unmapped_region: {
    total: number;
    permitted: number;
    in_progress: number;
    blocked: number;
    by_subcategory: {
      work_subcategory: string;
      total: number;
      permitted: number;
      in_progress: number;
      blocked: number;
    }[];
  };
  recent: {
    decision_id: number;
    area_id: number;
    label: string;
    kelurahan: string;
    rw: string | null;
    rt: string | null;
    state: DecisionState;
    author: string;
    synthetic: boolean;
    created_at: string;
  }[];
};

export type TemplateSlot = {
  id: number;
  key: string;
  label_id: string;
  kind: "consent" | "attestation" | "internal";
  cardinality: string;
  display_order: number;
  evidence_hint: string;
  // string, not DocumentCategory: retired rows keep their historical ODP/ODC
  // values (see docs/schema.sql), which are no longer a live category.
  applies_to: string | null;
  active: number;
};

export type DocumentAreaLink = {
  area_id: number;
  address_detail: string | null;
  kelurahan: string;
  kecamatan: string;
  rt: string | null;
  rw: string | null;
};

export type DocumentFile = {
  evidence_id: number;
  content_type: string | null;
  byte_size: number | null;
  content_sha256: string;
  created_at: string;
  author: string | null;
  areas: DocumentAreaLink[];
};

export type DocumentMissing = DocumentAreaLink & {
  document_key: string;
  document_label: string;
};

export type DocumentCategoryGroup = {
  category: DocumentCategory;
  document_key: string;
  document_label: string;
  files: DocumentFile[];
  missing: DocumentMissing[];
};

/**
 * Reference files (#51): background reading per work parent, grouped by
 * content hash with area links -- the same shape as the Database listing,
 * keyed by the work axis (#49) instead of the document axis. Areas with no
 * file are listed as missing, visibly -- never silently complete.
 */
export type ReferenceAreaLink = {
  area_id: number;
  address_detail: string | null;
  kelurahan: string;
  kecamatan: string;
  rt: string | null;
  rw: string | null;
  work_subcategory: WorkSubcategory | null;
};

export type ReferenceFile = {
  reference_id: number;
  content_type: string | null;
  byte_size: number | null;
  content_sha256: string;
  created_at: string;
  author: string | null;
  areas: ReferenceAreaLink[];
};

export type ReferenceMissing = ReferenceAreaLink;

export type ReferenceGroup = {
  work_category: WorkCategory;
  files: ReferenceFile[];
  missing: ReferenceMissing[];
};

export type AdminUser = {
  id: number;
  display_name: string;
  email: string;
  active: number;
  created_at: string;
  roles: string[];
};

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

async function req<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    credentials: "same-origin",
    ...init,
    headers: init?.body ? { "content-type": "application/json", ...init.headers } : init?.headers,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new ApiError(body.error ?? `Permintaan gagal (${res.status}).`, res.status);
  }
  return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

const post = <T,>(url: string, body: unknown) =>
  req<T>(url, { method: "POST", body: JSON.stringify(body) });
const patch = <T,>(url: string, body: unknown) =>
  req<T>(url, { method: "PATCH", body: JSON.stringify(body) });
const put = <T,>(url: string, body: unknown) =>
  req<T>(url, { method: "PUT", body: JSON.stringify(body) });

export type AddressInput = {
  rt?: string | null;
  rw?: string | null;
  kelurahan: string;
  kecamatan: string;
  kabupaten_kota?: string | null;
  postcode?: string | null;
  address_detail?: string | null;
  geocode_touched?: boolean;
};

export type EvidenceDraft = { kind: "photo" | "document"; byte_size: number };

export const api = {
  me: () => req<Me>("/api/me"),
  login: (email: string, password: string) =>
    post<{ user: Me["user"]; roles: string[]; capabilities: Cap[] }>("/api/auth/login", { email, password }),
  logout: () => post<{ ok: true }>("/api/auth/logout", {}),

  areas: () => req<AreaRow[]>("/api/areas"),
  area: (id: number) => req<AreaDetail>(`/api/areas/${id}`),
  createArea: (
    geometry: Poly,
    address: AddressInput,
    document_category: DocumentCategory,
    work_category: WorkCategory,
    work_subcategory: WorkSubcategory,
  ) =>
    post<{ area_id: number }>("/api/areas", { geometry, address, document_category, work_category, work_subcategory }),
  setWorkCategory: (id: number, work_category: WorkCategory, work_subcategory: WorkSubcategory) =>
    patch<{ ok: true }>(`/api/areas/${id}/work-category`, { work_category, work_subcategory }),
  setGeometry: (id: number, geometry: Poly) => patch<{ ok: true }>(`/api/areas/${id}/geometry`, { geometry }),
  setDocumentCategory: (id: number, document_category: DocumentCategory) =>
    patch<{ ok: true }>(`/api/areas/${id}/document-category`, { document_category }),
  setAddress: (id: number, address: AddressInput) => patch<{ ok: true }>(`/api/areas/${id}/address`, address),

  recordDecision: (
    areaId: number,
    input: {
      slot_id: number;
      state: DecisionState;
      note?: string;
      effective_at?: string;
      evidence?: EvidenceDraft[];
    },
  ) =>
    post<{ decision_id: number; evidence_ids: number[]; warning: string | null }>(
      `/api/areas/${areaId}/decisions`,
      input,
    ),

  plots: () => req<PlotView[]>("/api/plots"),
  createPlot: (
    geometry: Poly,
    owners: string[],
    meta: { label?: string | null; land_right?: string | null; registered?: boolean; evidence_ref?: string | null },
  ) => post<{ plot_id: number }>("/api/plots", { geometry, owners, ...meta }),
  updatePlot: (id: number, patch: { owners?: string[]; label?: string | null }) =>
    put<{ plot_id: number; owners: string[] }>(`/api/plots/${id}`, patch),

  deleteEvidence: (id: number, basis: string, note?: string) =>
    req<{ deleted: number }>(`/api/evidence/${id}`, {
      method: "DELETE",
      body: JSON.stringify({ basis, note }),
    }),

  dashboard: () => req<Dashboard>("/api/dashboard"),
  references: () => req<ReferenceGroup[]>("/api/references"),
  documents: () => req<DocumentCategoryGroup[]>("/api/documents"),
  template: () => req<TemplateSlot[]>("/api/template"),

  geocode: (q: string) =>
    req<{ results: AddressInput[]; suggested: boolean; error?: string }>(`/api/geocode?q=${encodeURIComponent(q)}`),

  users: () => req<AdminUser[]>("/api/admin/users"),
  createUser: (u: { display_name: string; email: string; password: string; roles: string[] }) =>
    post<{ id: number }>("/api/admin/users", u),
  updateUser: (id: number, u: { active?: boolean; password?: string; roles?: string[] }) =>
    patch<{ ok: true }>(`/api/admin/users/${id}`, u),
};

export const evidenceUrl = (id: number) => `/api/evidence/${id}`;
export const referenceUrl = (id: number) => `/api/references/${id}`;
